import React, { FC } from 'react';
import styled, { css } from 'styled-components';
import { getResponsiveType } from '../utils';
import { ThemeColorsKeys } from '../styles/tokens/colors';
import { StyledButton } from './Button';

interface TableProps {
  children: any;
}

interface StyledTableHeaderCellProps {
  sortable?: boolean;
  color?: ThemeColorsKeys;
  sticky?: boolean;
  stacked?: boolean;
}

interface StyledTableRowProps {
  active?: boolean;
  priority?: boolean;
  sticky?: boolean;
  stickyOffset?: string;
}

interface StyledTableCellProps {
  alignRight?: boolean;
  sticky?: boolean;
  wrap?: boolean;
}

const StyledTable = styled.table`
  ${({ theme: { global } }) => css`
    border-collapse: separate;
    border-spacing: 0;
    border-radius: ${global.borderRadius.default};
    width: 100%;
    white-space: nowrap;

    ${StyledButton} {
      width: 100%;
    }
  `};
`;

export const StyledTableCell = styled.td<StyledTableCellProps>`
  ${({ theme: { space, borders }, alignRight, sticky, wrap }) => css`
    padding: ${space.md};
    border-right: ${borders.default};
    ${getResponsiveType('bodySmall')}
    border-bottom: ${borders.default};
    background: #fff;

    &:first-child {
      border-left: ${borders.default};
    }

    ${alignRight &&
    css`
      text-align: right;

      > * {
        justify-content: flex-end;
      }
    `}

    // Sticky

    ${sticky &&
    css`
      position: sticky;
      left: 0;
    `}

    ${wrap &&
    css`
      white-space: normal;
    `}
  `};
`;

export const StyledTableHeaderCell = styled.th<StyledTableHeaderCellProps>`
  ${({ theme: { space, colors }, sortable, color, sticky, stacked }) => css`
    padding: ${space.md};
    border-right: 1px solid ${colors.grey1.default};
    background-color: ${colors.grey3.default};
    ${getResponsiveType('titleExtraSmall')}
    color: ${color ? colors[color].default : colors.black.default};

    &:last-child {
      border-right: 1px solid ${colors.grey3.default};
    }

    ${sortable &&
    css`
      padding: 0;
      button {
        background: inherit;
        color: inherit;
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        padding: ${space.md};
        cursor: pointer;
      }
    `}

    // Sticky

    ${sticky &&
    css`
      position: sticky;
      left: 0;
    `}

    // Stacked (two rows of headings)
    ${stacked &&
    css`
      border-bottom: 1px solid ${colors.grey1.default};
    `}
  `};
`;

export const StyledTableFooterCell = styled.td`
  ${({ theme: { space, colors } }) => css`
    padding: ${space.md};
    border-right: 1px solid ${colors.grey1.default};
    background-color: ${colors.grey3.default};
    ${getResponsiveType('titleExtraSmall')}
    color: ${colors.black.default};

    &:last-child {
      border-right: 1px solid ${colors.grey3.default};
    }
  `};
`;

export const StyledTableRow = styled.tr<StyledTableRowProps>`
  ${({ theme: { colors }, active, priority, sticky, stickyOffset }) => css`
    // Active row

    ${active &&
    css`
      ${StyledTableCell} {
        background-color: ${colors.grey1.default};
        ${getResponsiveType('titleExtraSmall')}
      }
    `}

    // Priority row

    ${priority &&
    css`
      ${StyledTableCell} {
        background-color: ${colors.grey1.default};
        ${getResponsiveType('titleSmall')}
      }
    `}

    // Sticky row

    ${sticky &&
    css`
      position: sticky;
      top: ${stickyOffset || '0'};
    `}
  `};
`;

export const StyledScrollableTable = styled.div`
  ${({ theme: { global } }) => css`
    overflow: auto;
    border-radius: ${global.borderRadius.default};
  `};
`;

export const Table: FC<React.PropsWithChildren<TableProps>> = ({ children, ...rest }) => (
  <StyledTable {...rest}>{children}</StyledTable>
);
